/* eslint-disable no-use-before-define */
import React, { useEffect, useState, useContext } from 'react';
import { LocaleContext } from 'providers/locale-settings-context';
import { uniqBy } from 'lodash';
import classnames from 'classnames';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import { Translation, useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import EventList from './EventList';
import {
  populateAndFormatSimplifiedEvents,
  formatAuditEvents,
} from '../../../../helpers/events';
import CargomaticInput from '../../../v2/input';

function labelFormatter(cell, row) {
  if (row.meta && row.meta.type && row.meta.type === 'rpa-update') {
    return cell;
  }
  if (typeof cell !== 'object') {
    return <Translation>{(t) => t(sluggify(cell))}</Translation>;
  }
  return cell;
}

const ALLOWED_DUPLICATE_EVENT_WHITELIST = ['approved', 'declined'];

const dropDuplicateEvents = (events) => {
  if (!Array.isArray(events)) {
    throw new Error('Input should be an array');
  }

  return uniqBy(events, ({ application, event, object, user, timestamp }) => {
    const userEmail = user?.email || '';
    return `${application}-${event}-${object}-${userEmail}-${timestamp}`;
  });
};

/*
 * text property is now localized slugs
 */
const simplifiedColumns = [
  {
    dataField: 'label',
    text: 'EVENT',
    formatter: labelFormatter,
  },
  {
    dataField: 'date',
    text: 'DATE',
  },
  {
    dataField: 'updated',
    text: 'ENTERED_LAST_UPDATE',
  },
  {
    dataField: 'source',
    text: 'SOURCE',
  },
  {
    dataField: 'channel',
    text: 'CHANNEL',
  },
];

/*
 * text property is now localized slugs
 */
const allColumns = [
  {
    dataField: 'label',
    text: 'EVENT',
    formatter: labelFormatter,
  },
  {
    dataField: 'date',
    text: 'DATE',
  },
  {
    dataField: 'reported_date',
    text: 'REPORTED_DATE',
  },
  {
    dataField: 'source',
    text: 'SOURCE',
  },
  {
    dataField: 'channel',
    text: 'CHANNEL',
  },
];

const Events = ({ shipment, audits, stops, keepDuplicateRows }) => {
  const { t } = useTranslation();
  const { formatDateTime } = useContext(LocaleContext);
  const [filters, setFilters] = useState({
    rowData: [],
    columnData: [],
    isSimplifiedView: true,
    eventName: '',
    filteredRows: null,
    activeTab: 'simplified',
  });

  useEffect(() => {
    setFilters({
      ...filters,
      rowData: getEvents(shipment, audits, stops, filters.isSimplifiedView),
      columnData: getColumns(filters.isSimplifiedView),
    });
  }, [shipment, audits]);

  const onEventChange = (searchedEvent) => {
    if (!searchedEvent) {
      setFilters({
        ...filters,
        eventName: '',
        filteredRows: null,
      });
    } else {
      const filteredRows = filters.rowData.filter((row) => {
        return row.event.toLowerCase().includes(searchedEvent.toLowerCase());
      });
      setFilters({
        ...filters,
        eventName: searchedEvent,
        filteredRows,
      });
    }
  };

  const getEvents = (shipment, audits = [], stops = [], simplified = true) => {
    if (!keepDuplicateRows) audits = dropDuplicateEvents(audits);

    if (simplified) {
      const type = determineJobType(shipment);

      if (!type) return [];

      const rows = populateAndFormatSimplifiedEvents(
        type,
        shipment,
        stops,
        audits,
      );
      return rows;
    }
    const rows = formatAuditEvents(formatDateTime, audits);
    return rows;
  };

  const drayageOrderTypes = [
    'import',
    'export',
    'reposition',
    'reload',
    'intermodal',
  ];

  const determineJobType = (shipment) => {
    const { shipment_type, order_type, order_movement_type } = shipment;
    if (!isIntermodalShipmentType(shipment_type)) {
      return 'shorthaul';
    }

    if (!order_type) {
      return null;
    }

    if (drayageOrderTypes.includes(order_type)) {
      return order_type;
    }

    if (!order_movement_type) {
      return null;
    }

    if (order_type === 'domestic' && order_movement_type === 'delivery') {
      return 'import';
    }
    if (order_type === 'domestic' && order_movement_type === 'pickup') {
      return 'export';
    }
  };

  const getColumns = (simple) => {
    return simple ? simplifiedColumns : allColumns;
  };

  const setToSimplifiedView = () => {
    if (!filters.isSimplifiedView) {
      setFilters({
        ...filters,
        rowData: getEvents(shipment, audits, stops, true),
        isSimplifiedView: true,
        columnData: getColumns(true),
        eventName: '',
        filteredRows: null,
      });
    }
  };

  const setToAllView = () => {
    if (filters.isSimplifiedView) {
      setFilters({
        ...filters,
        rowData: getEvents(shipment, audits, stops, false),
        isSimplifiedView: false,
        columnData: getColumns(false),
        eventName: '',
        filteredRows: null,
      });
    }
  };

  const findRows = () => {
    const { rowData, filteredRows } = filters;
    return filteredRows || rowData;
  };

  const renderTabs = () => {
    const activeTab = {
      'bg-white': true,
      'inline-block': true,
      'border-solid': true,
      'border-l': true,
      'border-t': true,
      'border-r': true,
      'border-grey-light': true,
      'rounded-t': true,
      'py-2': true,
      'px-4': true,
      'text-blue-dark': true,
      'font-semibold': true,
      'text-grey': false,
    };
    const tab = {
      'bg-white': true,
      'inline-block': true,
      'py-2': true,
      'px-4': true,
      'text-grey': true,
      'text-blue-hover:text-blue': true,
      'font-semibold': true,
      'no-underline': true,
    };
    return (
      <ul
        style={{
          marginLeft: '2px',
          textDecoration: 'none',
        }}
        className="flex no-underline border-b border-solid list-reset border-grey-light"
      >
        <li className="mr-1 -mb-px">
          <a
            style={{
              textDecoration: 'none',
            }}
            className={classnames({
              ...tab,
              ...(filters.isSimplifiedView === true ? activeTab : {}),
            })}
            onClick={() => setToSimplifiedView()}
          >
            {t('SIMPLIFIED')}
          </a>
        </li>
        <li className="mr-1">
          <a
            style={{
              textDecoration: 'none',
            }}
            className={classnames({
              ...tab,
              ...(filters.isSimplifiedView === false ? activeTab : {}),
            })}
            onClick={() => setToAllView()}
          >
            {t('ALL_AUDITS')}
          </a>
        </li>
      </ul>
    );
  };

  return (
    <div>
      {filters.rowData && filters.columnData && (
        <div>
          <div className="mb-2" />
          <div>
            {renderTabs()}
            <div className="mb-2" />
            {filters.rowData.length ? (
              <div>
                <CargomaticInput
                  className="w-60"
                  value={filters.eventName || ''}
                  placeholder={t('SEARCH_BY_EVENT_NAME')}
                  label={t('SEARCH')}
                  info={
                    filters.isSimplifiedView
                      ? t('SEARCH_SIMPLIFIED_EVENT')
                      : t('SEARCH_EVENT_AUDITS')
                  }
                  onChange={onEventChange}
                  name="event"
                  type="text"
                />
                <div className="mb-4" />
                <EventList
                  rows={findRows()}
                  columns={filters.columnData}
                  paginate={!filters.isSimplifiedView}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
